import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, MessageSquare, TrendingUp, AlertTriangle } from "lucide-react";

const MONTH_LABELS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
function monthLabel(yyyymm) {
  if (!yyyymm) return "";
  const [, m] = yyyymm.split("-");
  return MONTH_LABELS_IT[Number(m) - 1] || yyyymm;
}

function rateTone(rate) {
  if (rate === null || rate === undefined) return "zinc";
  if (rate >= 70) return "emerald";
  if (rate >= 40) return "amber";
  return "rose";
}
const TONES = {
  emerald: { text: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500", lightBg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-200 dark:border-emerald-900/50" },
  amber: { text: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500", lightBg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-200 dark:border-amber-900/50" },
  rose: { text: "text-rose-600 dark:text-rose-400", bg: "bg-rose-500", lightBg: "bg-rose-50 dark:bg-rose-950/40", border: "border-rose-200 dark:border-rose-900/50" },
  zinc: { text: "text-zinc-500 dark:text-zinc-400", bg: "bg-zinc-400", lightBg: "bg-zinc-50 dark:bg-zinc-800", border: "border-zinc-200 dark:border-zinc-700" },
};

// Health card shown above the regulars list. Shows business-wide accept rate,
// top 3 decline reason buckets, and a 6-month bar trend so the owner can spot
// patterns ("most clients say 'mattina' → maybe my afternoon slots are off").
export default function ProposalsHealthPanel({ businessId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    api.get(`/businesses/${businessId}/proposals/health?months=6`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [businessId]);

  if (loading) return null;
  if (!data || !data.proposals_sent) return null; // hide entirely if no proposals ever sent

  const tone = TONES[rateTone(data.accept_rate)];
  const maxBar = Math.max(1, ...data.monthly_trend.map((m) => m.sent));

  return (
    <section className="rounded-3xl bg-white ring-1 ring-zinc-100 p-5 md:p-6 dark:bg-zinc-900 dark:ring-zinc-800" data-testid="proposals-health-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400">
            <Activity className="h-3.5 w-3.5" /> Health proposte
          </div>
          <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-1 text-zinc-900 dark:text-zinc-100">
            Come vanno le tue proposte
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
            Negli ultimi {data.window_months} mesi · aiuta a capire perché alcuni clienti rifiutano.
          </p>
        </div>
        <div className={`rounded-2xl px-4 py-3 ${tone.lightBg} ${tone.border} border min-w-[150px]`} data-testid="health-overall-rate">
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500 dark:text-zinc-400">Tasso accettazione</div>
          <div className="flex items-baseline gap-1 mt-1">
            <div className={`font-display text-3xl tabular-nums ${tone.text}`}>{data.accept_rate ?? "—"}</div>
            {data.accept_rate !== null && data.accept_rate !== undefined && <div className={`text-sm ${tone.text}`}>%</div>}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 dark:text-zinc-400">
            {data.proposals_accepted} su {data.proposals_accepted + data.proposals_declined} risposte
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-5">
        {/* Top decline reasons */}
        <div data-testid="health-top-reasons">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400 mb-2">
            <MessageSquare className="h-3 w-3" /> Top motivi di rifiuto
          </div>
          {data.top_decline_reasons.length === 0 ? (
            <div className="rounded-xl bg-zinc-50 px-4 py-6 text-center text-xs text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              Nessun motivo di rifiuto registrato.<br />
              <span className="text-zinc-400 dark:text-zinc-500">Quando un cliente rifiuta una proposta può lasciarti un motivo.</span>
            </div>
          ) : (
            <ul className="space-y-2">
              {data.top_decline_reasons.map((r, idx) => (
                <li key={r.label} className="rounded-xl bg-zinc-50 px-3 py-2.5 dark:bg-zinc-800" data-testid={`reason-${idx}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{r.label}</span>
                    <span className="text-xs font-semibold tabular-nums text-zinc-700 dark:text-zinc-300 shrink-0">
                      {r.count} <span className="text-zinc-400 dark:text-zinc-500">({r.pct}%)</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                    <div className="h-full bg-rose-500 dark:bg-rose-400 transition-all" style={{ width: `${r.pct}%` }} />
                  </div>
                  {r.example && (
                    <div className="text-[11px] text-zinc-500 italic mt-1.5 dark:text-zinc-400 truncate" title={r.example}>
                      Es. "{r.example}"
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Monthly trend bar chart */}
        <div data-testid="health-trend">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400 mb-2">
            <TrendingUp className="h-3 w-3" /> Andamento ultimi {data.window_months} mesi
          </div>
          <div className="rounded-xl bg-zinc-50 px-3 py-3 dark:bg-zinc-800">
            <div className="flex items-end justify-between gap-1.5 h-28">
              {data.monthly_trend.map((m) => {
                const totalH = m.sent > 0 ? Math.max(8, Math.round((m.sent / maxBar) * 110)) : 4;
                const acceptedH = m.sent > 0 ? Math.round((m.accepted / m.sent) * totalH) : 0;
                const declinedH = m.sent > 0 ? Math.round((m.declined / m.sent) * totalH) : 0;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`trend-${m.month}`}>
                    <div
                      className="w-full max-w-[28px] rounded-md bg-zinc-300 dark:bg-zinc-700 overflow-hidden flex flex-col-reverse mx-auto relative"
                      style={{ height: totalH }}
                      title={`${m.sent} inviate · ${m.accepted} accettate · ${m.declined} rifiutate${m.accept_rate !== null ? ` · ${m.accept_rate}%` : ""}`}
                    >
                      <div className="bg-emerald-500 dark:bg-emerald-400 transition-all" style={{ height: acceptedH }} />
                      <div className="bg-rose-500 dark:bg-rose-400 transition-all" style={{ height: declinedH }} />
                    </div>
                    <div className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400">{monthLabel(m.month)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-3 mt-2 text-[10px]">
              <span className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-400"><span className="h-2 w-2 rounded-sm bg-emerald-500 dark:bg-emerald-400" /> Accettate</span>
              <span className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-400"><span className="h-2 w-2 rounded-sm bg-rose-500 dark:bg-rose-400" /> Rifiutate</span>
              <span className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-400"><span className="h-2 w-2 rounded-sm bg-zinc-300 dark:bg-zinc-700" /> In attesa</span>
            </div>
          </div>
        </div>
      </div>

      {/* Insight pill — surfaces only if accept rate is below 40% */}
      {data.accept_rate !== null && data.accept_rate < 40 && (
        <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-xs text-rose-900 inline-flex items-center gap-2 dark:bg-rose-950/40 dark:border-rose-900/50 dark:text-rose-200" data-testid="health-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          Tasso di rifiuto alto. Prova a proporre slot diversi o chiedere ai clienti il loro orario preferito.
        </div>
      )}
    </section>
  );
}
